import { useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const COMBO_TIMEOUT = 600; // ms to wait for second key in a combo

export const useKeyboardShortcuts = ({ onToggleSidebar, onToggleNotifications } = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const pendingKey = useRef(null);
  const timerRef = useRef(null);
  const isLearner = user?.role === "learner";
  const isManager = user?.role === "manager";

  const navRoutes = {
    d: isLearner ? "/learner/dashboard" : isManager ? "/manager/dashboard" : "/admin/dashboard",
    c: isLearner ? "/my-courses" : isManager ? "/my-courses" : "/admin/courses",
    p: isLearner ? "/my-progress" : isManager ? "/my-progress" : "/admin/analytics",
    s: isLearner ? "/learner/settings" : isManager ? "/manager/settings" : "/admin/revenue",
    h: isLearner ? "/learner/support" : isManager ? "/manager/support" : "/admin/support",
    n: isLearner ? "/learner/notifications" : isManager ? "/manager/notifications" : "/admin/notifications",
  };

  const clearPending = useCallback(() => {
    pendingKey.current = null;
    clearTimeout(timerRef.current);
  }, []);

  useEffect(() => {
    const isEditing = () => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = el.tagName.toLowerCase();
      return tag === "input" || tag === "textarea" || tag === "select" || el.isContentEditable;
    };

    const handler = (e) => {
      const key = e.key.toLowerCase();
      const meta = e.metaKey || e.ctrlKey;

      // ⌘+B — Toggle sidebar (works even in inputs)
      if (meta && key === "b") {
        e.preventDefault();
        onToggleSidebar?.();
        clearPending();
        return;
      }

      // ⌘+K — Command palette / focus search (works even in inputs)
      if (meta && key === "k") {
        e.preventDefault();
        const search = document.querySelector('[data-testid="header-search-input"]') || document.querySelector('input[placeholder*="Search"]');
        search?.focus();
        clearPending();
        return;
      }

      // Block remaining shortcuts when typing in inputs
      if (isEditing()) return;

      // "?" — Go to keyboard shortcuts (Shift+/)
      if (key === "?" || (e.shiftKey && key === "/")) {
        e.preventDefault();
        navigate("/keyboard-shortcuts");
        clearPending();
        return;
      }

      // Ignore if meta/ctrl/alt held (except above)
      if (meta || e.altKey) return;

      // "/" — Focus search bar
      if (key === "/") {
        e.preventDefault();
        const search = document.querySelector('[data-testid="header-search-input"]') || document.querySelector('input[placeholder*="Search"]');
        search?.focus();
        clearPending();
        return;
      }

      // "G" combos — two-key navigation
      if (pendingKey.current === "g") {
        clearPending();
        const route = navRoutes[key];
        if (route) {
          e.preventDefault();
          navigate(route);
        }
        return;
      }

      if (key === "g") {
        pendingKey.current = "g";
        timerRef.current = setTimeout(clearPending, COMBO_TIMEOUT);
        return;
      }

      // Single-key: N — Toggle notifications
      if (key === "n" && !pendingKey.current) {
        onToggleNotifications?.();
        return;
      }

      clearPending();
    };

    window.addEventListener("keydown", handler);
    return () => {
      window.removeEventListener("keydown", handler);
      clearTimeout(timerRef.current);
    };
  }, [navigate, navRoutes, clearPending, onToggleSidebar, onToggleNotifications]);
};
